import React, { useState } from 'react';
import { Button, Space, message } from 'antd';
import { DownloadOutlined, HistoryOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PORTAL_EXPORT_ORDERS } from 'graphql/mutations/orderExport';
import { PortalOrderExportResult } from 'graphql/types';
import OrderExportProgressModal from 'components/OrderExportProgressModal';
import OrderTotalLabel from 'components/OrderTotalLabel';
import { useOrderExportError } from 'hooks/useOrderExportError';
import { getBrowserTimeZone } from 'utils/dateTime';
import { EXPORT_RECORDS_PATH, MAX_EXPORT_RECORDS, resolveUiLanguage } from 'utils/orderExport';

type Props = {
    /** 当前条件命中的总数，用于「导出全部」的前端预拦，同时在合计文案里展示 */
    totalElements: number;
    /** 深翻页截断后可查看的条数；未被截断时传 null。只影响合计文案，不影响导出范围 */
    viewableCap: number | null;
    /** 已提交的筛选条件，与列表当前展示的是同一份 */
    filter: Record<string, unknown>;
    /** 已勾选的运单号；空数组表示走条件导出。顺序不影响正确性，服务端对行序不作保证 */
    selectedTrackingNumbers: string[];
    onClearSelection: () => void;
};

/**
 * 订单列表页上方的导出工具条：已选计数 / 清空 / 合计条数 / 导出记录入口 / 导出按钮 + 等待弹窗。
 *
 * <p>整块由调用方按 portal:order-query:export 权限决定是否渲染，本组件不重复判权限。
 *
 * <p>拆成独立组件而不是塞进 OrderQueryPage：那个组件已有 380 行，
 * 而这里还要再带一个 mutation、一个弹窗状态和一套错误分支。
 */
const OrderExportToolbar: React.FC<Props> = ({
    totalElements,
    viewableCap,
    filter,
    selectedTrackingNumbers,
    onClearSelection,
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const translateError = useOrderExportError();
    const [taskId, setTaskId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [submit, { loading }] = useMutation<{ portalExportOrders: PortalOrderExportResult }>(
        PORTAL_EXPORT_ORDERS
    );

    const selectedCount = selectedTrackingNumbers.length;
    const hasSelection = selectedCount > 0;

    const handleExport = async () => {
        /*
         * 前端预拦：勾选模式的条数天然 ≤ 深翻页上限，不可能超限，只有「导出全部」需要拦。
         * 这层只为省一次注定被拒的往返，服务端提交阶段仍会 probe 命中总数独立校验——
         * 列表数据可能已经陈旧，真正说了算的是服务端那次。
         */
        if (!hasSelection && totalElements > MAX_EXPORT_RECORDS) {
            message.warning(translateError('EXCEEDS_LIMIT', null));
            return;
        }

        let result: PortalOrderExportResult | undefined;
        try {
            const { data } = await submit({
                variables: {
                    /*
                     * 导全部传 null 而不是空数组。两者服务端行为其实一样（dedupe 后都是空列表、
                     * 都判 FILTERED），传 null 只是让「没有勾选」在报文里一眼可辨，别指望它有行为差异。
                     */
                    trackingNumbers: hasSelection ? selectedTrackingNumbers : null,
                    /*
                     * 勾选模式下服务端完全忽略 filter，连落库都不落（buildPendingTask 里
                     * filterJson 只在 FILTERED 模式写）。这里仍原样带上只是为了两种模式共用一套
                     * variables，不要以为任务表里能查到勾选导出当时的筛选条件。
                     */
                    filter,
                    timeZone: getBrowserTimeZone(),
                    uiLanguage: resolveUiLanguage(i18n.language),
                },
            });
            result = data?.portalExportOrders;
        } catch {
            /* 网络异常与 GraphQL 错误已由 errorLink 弹过提示，这里不再弹第二条 */
            return;
        }

        if (!result?.success || !result.taskId) {
            message.error(translateError(result?.errorCode, result?.messageKey));
            return;
        }
        setTaskId(result.taskId);
        setModalOpen(true);
    };

    /*
     * 关掉弹窗即停止轮询。任务仍在跑时要告诉客户去哪儿取文件——它在服务端继续生成，
     * 不会因为前端不看了就中断；已经完成（甚至已下载）时不弹这句，那只会让人以为文件没生成出来。
     */
    const handleCloseModal = (stillRunning: boolean) => {
        setModalOpen(false);
        if (stillRunning) {
            message.info(t('orderExport.submitted'));
        }
    };

    /* data-testid 是测试定位锚点：断言合计文案落在工具条而非分页器里，比摸 antd 内部 class 稳 */
    return (
        <div data-testid="order-export-toolbar" className="flex flex-wrap items-center justify-between gap-2 mt-4">
            <Space>
                <span className="text-sm" style={{ color: 'rgba(0, 0, 0, 0.88)' }}>
                    {t('orderExport.selectedCount', { count: selectedCount })}
                </span>
                {hasSelection && (
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={onClearSelection}>
                        {t('orderExport.action.clearSelection')}
                    </Button>
                )}
                <span className="text-sm text-gray-300">|</span>
                <OrderTotalLabel total={totalElements} viewableCap={viewableCap} />
            </Space>
            <Space>
                <Button icon={<HistoryOutlined />} onClick={() => navigate(EXPORT_RECORDS_PATH)}>
                    {t('orderExport.action.records')}
                </Button>
                {/* 不勾选也可点：此时导的是当前条件下的全部订单 */}
                <Button type="primary" icon={<DownloadOutlined />} loading={loading} onClick={handleExport}>
                    {hasSelection
                        ? t('orderExport.action.exportSelected', { count: selectedCount })
                        : t('orderExport.action.exportAll')}
                </Button>
            </Space>

            <OrderExportProgressModal open={modalOpen} taskId={taskId} onClose={handleCloseModal} />
        </div>
    );
};

export default OrderExportToolbar;
